/**
 * Skill Engine - DSL, Parsing und Matching-Logik
 */

import type { Employee, SkillLevel, SkillNode, SkillMatchResult } from "../types";
import { getSkillsArray, getEmojiToSkillMap, getSkillToEmojiMap } from "../stores";

// ==========================================
// Skill Requirement DSL - Fluent API Builder
// ==========================================

class SkillBuilder {
  private node: SkillNode;

  constructor(emojiString: string, effort: number = 1.0) {
    this.node = {
      type: 'skill',
      skills: emojiString,
      effort,
    };
  }

  or(other: SkillBuilder | SkillNode): SkillBuilder {
    const otherNode = other instanceof SkillBuilder ? other.build() : other;
    const newBuilder = new SkillBuilder("");
    newBuilder.node = {
      type: 'or',
      children: [this.node, otherNode],
      effort: 1.0,
    };
    return newBuilder;
  }

  and(other: SkillBuilder | SkillNode): SkillBuilder {
    const otherNode = other instanceof SkillBuilder ? other.build() : other;
    const newBuilder = new SkillBuilder("");
    newBuilder.node = {
      type: 'all',
      children: [this.node, otherNode],
      effort: 1.0,
    };
    return newBuilder;
  }

  effort(multiplier: number): SkillBuilder {
    this.node.effort = multiplier;
    return this;
  }

  build(): SkillNode {
    return this.node;
  }
}

// Factory-Funktion für die DSL
export function skill(emojiString: string, effort: number = 1.0): SkillBuilder {
  return new SkillBuilder(emojiString, effort);
}

// ==========================================
// Skill Parsing
// ==========================================

// Hilfsfunktion: Parst Emoji-String zu Skill-Counts
// Helper to segment emojis with fallback for older environments
function segmentEmojis(str: string): string[] {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new (Intl as any).Segmenter();
    return [...segmenter.segment(str)].map((s: any) => s.segment);
  }
  return [...str];
}

export function parseEmojiToSkillCounts(emojiString: string): Record<string, number> {
  const counts: Record<string, number> = {};
  if (!emojiString) return counts;

  const emojiToSkill = getEmojiToSkillMap();
  const segments = segmentEmojis(emojiString);
  for (const emoji of segments) {
    const skillId = emojiToSkill[emoji];
    if (skillId) {
      counts[skillId] = (counts[skillId] || 0) + 1;
    }
  }
  return counts;
}

// Parst einen Emoji-String und zählt die Vorkommen jedes Skills (max 3)
export function parseSkillsFromEmojis(emojiString: string | undefined): Record<string, SkillLevel> {
  const skills: Record<string, number> = {};
  const skillDefinitions = getSkillsArray();
  const emojiToSkill = getEmojiToSkillMap();

  // Initialisiere alle Skills mit 0
  for (const skill of skillDefinitions) {
    skills[skill.id] = 0;
  }

  if (!emojiString) return skills as Record<string, SkillLevel>;

  // Segmentiere den String in Grapheme (damit Emojis korrekt erkannt werden)
  const segments = segmentEmojis(emojiString);

  for (const emoji of segments) {
    const skillId = emojiToSkill[emoji];
    if (skillId && skills[skillId] !== undefined) {
      skills[skillId] = Math.min(3, skills[skillId] + 1); // Max Level 3
    }
  }

  return skills as Record<string, SkillLevel>;
}

// Konvertiert Skills-Record zurück zu Emoji-String
export function skillsToEmojiString(skills: Record<string, SkillLevel>): string {
  const skillToEmoji = getSkillToEmojiMap();
  const parts: string[] = [];

  for (const [skillId, level] of Object.entries(skills)) {
    const emoji = skillToEmoji[skillId];
    if (emoji && level > 0) {
      // Wiederhole das Emoji entsprechend dem Level
      parts.push(emoji.repeat(level));
    }
  }

  return parts.join("");
}

// ==========================================
// Team Skill Kombination (Maximum-Prinzip)
// ==========================================

/**
 * Kombiniert die Skills mehrerer Mitarbeiter nach dem Maximum-Prinzip.
 */
export function combineTeamSkills(
  memberSkills: Record<string, SkillLevel>[]
): Record<string, SkillLevel> {
  const combined: Record<string, number> = {};
  const skillDefinitions = getSkillsArray();

  for (const skill of skillDefinitions) {
    combined[skill.id] = 0;
  }

  for (const skills of memberSkills) {
    for (const [skillId, level] of Object.entries(skills)) {
      if (level > (combined[skillId] ?? 0)) {
        combined[skillId] = level;
      }
    }
  }

  return combined as Record<string, SkillLevel>;
}

/**
 * Kombiniert die Skills mehrerer Mitarbeiter aus ihren Emoji-Strings.
 */
export function combineTeamSkillsFromEmojis(
  emojiStrings: (string | undefined)[]
): Record<string, SkillLevel> {
  const memberSkills = emojiStrings.map(parseSkillsFromEmojis);
  return combineTeamSkills(memberSkills);
}

// ==========================================
// Skill Evaluation Engine
// ==========================================

function evaluateSkillNode(
  node: SkillNode,
  skills: Record<string, SkillLevel>
): SkillMatchResult {
  const nodeEffort = node.effort ?? 1.0;

  if (node.type === 'skill') {
    const requiredCounts = parseEmojiToSkillCounts(node.skills || "");

    for (const [skillId, requiredLevel] of Object.entries(requiredCounts)) {
      const actualLevel = skills[skillId] ?? 0;
      if (actualLevel < requiredLevel) {
        return { matches: false, effort: nodeEffort, matchedPath: node.skills };
      }
    }
    return { matches: true, effort: nodeEffort, matchedPath: node.skills };
  }

  if (node.type === 'or' && node.children) {
    let bestMatch: SkillMatchResult | null = null;

    for (const child of node.children) {
      const result = evaluateSkillNode(child, skills);
      if (result.matches) {
        const totalEffort = nodeEffort * result.effort;
        if (!bestMatch || totalEffort < bestMatch.effort) {
          bestMatch = {
            matches: true,
            effort: totalEffort,
            matchedPath: result.matchedPath
          };
        }
      }
    }

    return bestMatch || { matches: false, effort: nodeEffort };
  }

  if (node.type === 'all' && node.children) {
    let totalEffort = nodeEffort;
    const matchedPaths: string[] = [];

    for (const child of node.children) {
      const result = evaluateSkillNode(child, skills);
      if (!result.matches) {
        return { matches: false, effort: totalEffort };
      }
      totalEffort *= result.effort;
      if (result.matchedPath) {
        matchedPaths.push(result.matchedPath);
      }
    }

    return {
      matches: true,
      effort: totalEffort,
      matchedPath: matchedPaths.join(" + ")
    };
  }

  return { matches: false, effort: 1.0 };
}

/**
 * Prüft ein Skill-Requirement gegen die Skills einer Person oder eines Teams.
 */
export function evaluateRequirement(
  requirement: string | SkillNode,
  skills: Record<string, SkillLevel>
): SkillMatchResult {
  if (typeof requirement === 'string') {
    return evaluateSkillNode({ type: 'skill', skills: requirement, effort: 1.0 }, skills);
  }
  return evaluateSkillNode(requirement, skills);
}

/**
 * Prüft ein Skill-Requirement direkt gegen mehrere Mitarbeiter (Team-Matching).
 */
export function evaluateTeamRequirement(
  requirement: string | SkillNode,
  employees: Employee[]
): SkillMatchResult {
  const teamSkills = combineTeamSkillsFromEmojis(employees.map(e => e.skills));
  return evaluateRequirement(requirement, teamSkills);
}

/**
 * Analysiert welche Skills einem Team fehlen.
 */
export function analyzeTeamSkillGaps(
  requirement: string | SkillNode,
  employees: Employee[]
): { skillId: string; skillName: string; emoji: string; required: number; actual: number; missing: number }[] {
  const teamSkills = combineTeamSkillsFromEmojis(employees.map(e => e.skills));
  const gaps: { skillId: string; skillName: string; emoji: string; required: number; actual: number; missing: number }[] = [];

  const collectRequiredSkills = (node: SkillNode): Record<string, number> => {
    if (node.type === 'skill') {
      return parseEmojiToSkillCounts(node.skills || "");
    }
    if (node.children) {
      const childRequirements = node.children.map(collectRequiredSkills);
      if (node.type === 'or') {
        return childRequirements[0] || {};
      }
      const combined: Record<string, number> = {};
      for (const req of childRequirements) {
        for (const [skillId, level] of Object.entries(req)) {
          combined[skillId] = Math.max(combined[skillId] || 0, level);
        }
      }
      return combined;
    }
    return {};
  };

  const requiredSkills = typeof requirement === 'string'
    ? parseEmojiToSkillCounts(requirement)
    : collectRequiredSkills(requirement);

  const skillDefinitions = getSkillsArray();
  for (const [skillId, required] of Object.entries(requiredSkills)) {
    const actual = teamSkills[skillId] ?? 0;
    if (actual < required) {
      const skillDef = skillDefinitions.find(s => s.id === skillId);
      gaps.push({
        skillId,
        skillName: skillDef?.name ?? skillId,
        emoji: skillDef?.emoji ?? "?",
        required,
        actual,
        missing: required - actual,
      });
    }
  }

  return gaps;
}

/**
 * Rendert einen SkillNode als lesbaren String.
 */
export function renderSkillNode(node: SkillNode, depth = 0): string {
  const effortSuffix = node.effort && node.effort !== 1.0
    ? ` (×${node.effort.toFixed(1)})`
    : "";

  if (node.type === 'skill') {
    return `${node.skills}${effortSuffix}`;
  }

  if (node.type === 'or' && node.children) {
    const parts = node.children.map(c => renderSkillNode(c, depth + 1));
    const joined = parts.join(" ODER ");
    return depth > 0 ? `(${joined})${effortSuffix}` : joined + effortSuffix;
  }

  if (node.type === 'all' && node.children) {
    const parts = node.children.map(c => renderSkillNode(c, depth + 1));
    const joined = parts.join(" UND ");
    return depth > 0 ? `(${joined})${effortSuffix}` : joined + effortSuffix;
  }

  return "?";
}
